import React, { useEffect, useMemo, useRef, useState } from 'react';
import { extractSocialVerses, shuffleSocialVerses, recommendSocialVerses, makeSocialCards, verseTopics, readSocialInterests, rememberSocialRead, SOCIAL_INTEREST_KEY, SOCIAL_TOPICS } from '../utils/socialBible';
import './BibleSocial.css';

const EDITIONS = { norm: '口語訳', ruby: '口語訳・ルビ', en: 'English', gal: 'ギャル訳', hebrew: 'ヘブライ語', greek: 'ギリシャ語', latin: 'ラテン語' };
function Icon({ name }) {
  const paths = {
    thread: 'M5 4h14v11H9l-4 4V4Z',
    quote: 'M4 6h6v7H6v4H3v-4l1-7Zm10 0h6v7h-4v4h-3v-4l1-7Z',
    save: 'M6 3h12v18l-6-4-6 4V3Z',
    share: 'M12 15V3m-4 4 4-4 4 4M5 12v8h14v-8',
    refresh: 'M20 7v5h-5M4 17v-5h5M5 8a7 7 0 0 1 12-3l3 3M4 16l3 3a7 7 0 0 0 12-3',
    book: 'M12 5v15M12 5C9 3 5 3 2 4v15c3-1 7-1 10 1 3-2 7-2 10-1V4c-3-1-7-1-10 1Z',
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name]} /></svg>;
}

function FeedCard({ card, book, bookName, type, onCommentary, onMessage }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const item = card.item;
  useEffect(() => {
    if (!window.IntersectionObserver) return;
    let timer;
    let recorded = false;
    const observer = new IntersectionObserver(([entry]) => {
      clearTimeout(timer);
      if (entry.isIntersecting && !recorded) timer = setTimeout(() => {
        if (document.hidden) return;
        rememberSocialRead(item.text); recorded = true;
      }, 2500);
    }, { threshold: 0.6 });
    observer.observe(ref.current);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [item]);
  const href = verse => `/bible/${book}/${verse.chapter}/${verse.verse}?type=${type}`;
  const byline = verse => <div className="bible-social__byline"><strong>{bookName}</strong><span>@{book}</span><a href={href(verse)}>{verse.reference} ↗</a></div>;
  const text = verse => <p className="bible-social__verse" dir={type === 'hebrew' ? 'rtl' : 'auto'}>{verse.text}</p>;
  const topics = verseTopics(item.text);
  return <article className={`bible-social__card bible-social__card--${card.kind}`} ref={ref}>
    <div className="bible-social__post">
      <div className={`bible-social__avatar ${card.kind === 'quote' ? 'bible-social__avatar--note' : ''}`} aria-hidden="true">{card.kind === 'quote' ? '✦' : <Icon name="book" />}</div>
      <div className="bible-social__post-content">
        {card.kind === 'quote' ? <>
          <div className="bible-social__byline"><strong>ことばの余白</strong><span>編集ノート · QT</span></div>
          <p className="bible-social__verse">{card.topic.note}</p>
          <a className="bible-social__quote" href={href(item)}><span><Icon name="book" />{bookName} <span>{item.reference} · {EDITIONS[type]}</span></span>{text(item)}</a>
        </> : <>{byline(item)}{text(item)}</>}
        {topics.length > 0 && <div className="bible-social__tags">{topics.slice(0, 3).map(topic => <span key={topic.id}>#{topic.label}</span>)}</div>}
        <div className="bible-social__actions">
          <a href={href(item)} aria-label={`${item.reference}を本文で読む`}><Icon name="book" /><span>本文</span></a>
          {onCommentary && <button type="button" className="reformation-verse-button" onClick={() => onCommentary({ chapter: item.chapter, verse: item.verse, verseText: item.text })} aria-label={`${item.reference}の註解を開く`}><Icon name="thread" /><span>註解</span></button>}
          <button type="button" aria-label={`${item.reference}のリンクをコピー`} onClick={async () => {
            try { await navigator.clipboard.writeText(new URL(href(item), window.location.origin).href); onMessage('聖句のリンクをコピーしました。'); }
            catch { onMessage('コピーできませんでした。「本文」から聖句を開けます。'); }
          }}><Icon name="share" /><span>リンク</span></button>
        </div>
      </div>
    </div>
    {card.kind === 'thread' && <>
      {(expanded ? card.verses.slice(1) : card.verses.slice(1, 2)).map(verse => <div className="bible-social__post bible-social__post--reply" key={verse.id}><div className="bible-social__avatar" aria-hidden="true"><Icon name="book" /></div><div className="bible-social__post-content">{byline(verse)}{text(verse)}</div></div>)}
      {card.verses.length > 2 && <button className="bible-social__thread-more" type="button" aria-expanded={expanded} onClick={() => { setExpanded(!expanded); rememberSocialRead(item.text); }}>{expanded ? 'スレッドをたたむ' : 'このスレッドを読む'} <span aria-hidden="true">{expanded ? '↑' : '↓'}</span></button>}
    </>}
  </article>;
}

export default function BibleSocial({ html, book, bookName, type, onCommentary }) {
  const verses = useMemo(() => extractSocialVerses(html), [html]);
  const [tab, setTab] = useState('recommended');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [limit, setLimit] = useState(12);
  const [message, setMessage] = useState('');
  const [interests, setInterests] = useState(readSocialInterests);
  const previous = useRef('');
  const headerRef = useRef(null);
  useEffect(() => {
    setLoading(true); setLimit(12);
    const timer = setTimeout(() => {
      const key = `bible-social-first:${book}:${type}`;
      let last = previous.current;
      try { last = sessionStorage.getItem(key) || last; } catch { /* Session storage is optional. */ }
      const profile = readSocialInterests();
      setInterests(profile);
      const ordered = tab === 'recommended' ? recommendSocialVerses(verses, profile, last) : shuffleSocialVerses(verses, last);
      previous.current = ordered[0]?.id || '';
      try { sessionStorage.setItem(key, previous.current); } catch { /* Session storage is optional. */ }
      setCards(makeSocialCards(ordered, verses)); setLoading(false);
    }, 1600);
    return () => clearTimeout(timer);
  }, [verses, book, type, refresh, tab]);
  const words = SOCIAL_TOPICS.filter(topic => interests[topic.id] > 0).sort((a, b) => interests[b.id] - interests[a.id]).slice(0, 3);

  return <div className="bible-social">
    <header className="bible-social__header" ref={headerRef}><div><span className="bible-social__eyebrow">SCRIPTURE / TIMELINE</span><h1>ことばに、出会う。</h1><p>{bookName}<span> · {EDITIONS[type]}</span></p></div><span className="bible-social__mark" aria-hidden="true">✦</span></header>
    <div className="bible-social__intro">スクロールする日常に、ひとつの聖句を。</div>
    <nav className="bible-social__tabs" aria-label="SNSの読み方">{[['recommended', 'おすすめ'], ['random', 'ランダム']].map(([id, label]) => <button type="button" aria-pressed={tab === id} key={id} onClick={() => setTab(id)}>{label}</button>)}</nav>
    <details className="bible-social__preferences"><summary>{words.length ? `気になることば：${words.map(word => word.label).join('・')}` : 'あなたの読む時間から、次の出会いへ'}</summary><p>このSNS画面でしばらく読んだ聖句の単語を、この端末に保存しておすすめに使います。他サイトの履歴は使いません。QTはあらかじめ用意した編集ノートです。</p><button type="button" onClick={() => {
      try { localStorage.removeItem(SOCIAL_INTEREST_KEY); setInterests({}); setMessage('おすすめの履歴をリセットしました。'); setRefresh(value => value + 1); }
      catch { setMessage('履歴をリセットできませんでした。'); }
    }}>おすすめ履歴をリセット</button></details>
    <div className="bible-social__feed-heading"><span>聖句、スレッド、ときどきQT。</span><button type="button" disabled={loading} onClick={() => setRefresh(value => value + 1)}><Icon name="refresh" />新しいことば</button></div>
    <div className="bible-social__status" role="status">{message}</div>
    {loading ? <div aria-busy="true" className="bible-social__loading"><p role="status">ことばを探しています…</p>{[0, 1, 2].map(value => <div className="bible-social__skeleton" key={value}><i /><div><i /><i /><i /></div></div>)}</div> : <>
      {!cards.length && <p className="bible-social__empty">この箇所の聖句はまだ表示できません。上の訳選択から本文をお読みください。</p>}
      {cards.slice(0, limit).map(card => <FeedCard key={`${refresh}-${tab}-${card.item.id}`} card={card} book={book} bookName={bookName} type={type} onCommentary={onCommentary} onMessage={setMessage} />)}
      {cards.length > limit ? <button className="bible-social__more" type="button" onClick={() => setLimit(value => value + 12)}>続きを読み込む ↓</button> : cards.length > 0 && <div className="bible-social__end"><p>この書のことばを、また違う順番で。</p><button type="button" onClick={() => { setRefresh(value => value + 1); headerRef.current?.scrollIntoView({ block: 'start' }); }}>もう一度出会う ↻</button></div>}
    </>}
  </div>;
}
